import React from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
} from '@mui/material'
import {
  HealthAndSafety,
  Medication,
  Assignment,
  Science,
  LocalHospital,
  Info,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import axios from 'axios'

import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'

interface HealthDataStats {
  diseases: number
  medications: number
  symptoms: number
  testResults: number
  surgeries: number
  others: number
}

const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  const { data: stats, isLoading } = useQuery<HealthDataStats>(
    'healthStats',
    async () => {
      const response = await axios.get('/health-data')
      const data = response.data.data
      return {
        diseases: data.diseases.length,
        medications: data.medications.length,
        symptoms: data.symptoms.length,
        testResults: data.testResults.length,
        surgeries: data.surgeries.length,
        others: data.others.length,
      }
    }
  )

  const healthDataCards = [
    {
      title: 'Diseases & Conditions',
      count: stats?.diseases || 0,
      icon: <HealthAndSafety />,
      color: '#e74c3c',
      path: '/health-data?tab=diseases',
    },
    {
      title: 'Medications',
      count: stats?.medications || 0,
      icon: <Medication />,
      color: '#3498db',
      path: '/health-data?tab=medications',
    },
    {
      title: 'Symptoms',
      count: stats?.symptoms || 0,
      icon: <Assignment />,
      color: '#f39c12',
      path: '/health-data?tab=symptoms',
    },
    {
      title: 'Test Results',
      count: stats?.testResults || 0,
      icon: <Science />,
      color: '#9b59b6',
      path: '/health-data?tab=testResults',
    },
    {
      title: 'Procedures',
      count: stats?.surgeries || 0,
      icon: <LocalHospital />,
      color: '#1abc9c',
      path: '/health-data?tab=surgeries',
    },
    {
      title: 'Other Health Data',
      count: stats?.others || 0,
      icon: <Info />,
      color: '#95a5a6',
      path: '/health-data?tab=others',
    },
  ]

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <LoadingSpinner message="Loading your health data..." />
      </Box>
    )
  }

  return (
    <Box>
      {/* Welcome Section with Logo */}
      <Grid container spacing={3} mb={4} alignItems="center">
        <Grid item xs={12} md={8}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Welcome back, {user?.firstName}!
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Enter your health data to discover relevant clinical trials, the more accurate and complete the data is, the more accurate the trial matching will be.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box display="flex" justifyContent="flex-end">
            <Box display="flex" flexDirection="column" alignItems="center">
              <img 
                src="/logo.png" 
                alt="BlueBit Logo" 
                style={{ 
                  height: '60px', 
                  width: 'auto',
                  marginBottom: '4px',
                  display: 'block'
                }} 
              />
              <Typography variant="h6" component="div" fontWeight="bold" textAlign="center">
                <Box component="span" sx={{ color: '#1976d2' }}>BlueBit</Box>
                {' '}
                <Box component="span" sx={{ color: 'primary.main' }}>TrialFinder</Box>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Add Health Data Card */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" gutterBottom>
                Complete Your Health Profile
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                The more complete your profile, the better we can match you with relevant clinical trials.
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              onClick={() => navigate('/health-data')}
            >
              Add Health Data
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Health Data Overview */}
      <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
        Your Health Data
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom mb={3}>
        Click on any category to view or edit your health information
      </Typography>

      <Grid container spacing={3}>
        {healthDataCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
              }}
              onClick={() => navigate(card.path)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: card.color,
                      color: 'white',
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {card.count}
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  {card.title}
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(card.path)
                  }}
                >
                  View Details →
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Call to Action */}
      <Card sx={{ mt: 4, bgcolor: 'background.paper', border: '2px dashed', borderColor: 'primary.main' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Ready to Find Clinical Trials?
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Complete your health profile to get personalized clinical trial recommendations
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{ mt: 2 }}
            onClick={() => navigate('/health-data')}
          >
            Start Building Your Profile
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}

export default Dashboard
